import { useState, useEffect, useCallback, useMemo } from 'react';

import type {
  ApprovalAction,
  ApprovalDataEntity,
  ApprovalType,
} from 'ui-product-approvals-common';

import { genericErrorAlert } from '../utils/alertUtils';
import { captureException } from '../utils/sentryUtils';
import { useGetLatestApprovalQuery } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.hooks';
import { APPROVAL_TYPE_TO_OBJECT_TYPE_ID } from 'ui-product-approvals-common/constants/approvalTypeIds';
import { useAppHubUsers } from './useAppHubUsers';
import { useBreezeAgents } from './useBreezeAgents';
import { useAppTeams } from './useAppTeams';
import { toApprovalData } from '../utils/approvalResponseAdapters';
import { hasPendingAgentApprovalStep } from '../utils/approvalStepsUtils';
import { ACTOR } from '../constants/actors';
import { useAgentApprovalPolling } from './useAgentApprovalPolling';
import { useCreateApproval } from './useCreateApproval';
import { useApproveApproval } from './useApproveApproval';
import { useRejectApproval } from './useRejectApproval';
import { useCancelApproval } from './useCancelApproval';
import { useUpdateApproval } from './useUpdateApproval';
import { useRequestChangesOnApproval } from './useRequestChangesOnApproval';
import { useReRequestReview } from './useReRequestReview';
import { useApproveApprovalStep } from './useApproveApprovalStep';
import { useRejectApprovalStep } from './useRejectApprovalStep';
import { useRequestChangesOnApprovalStep } from './useRequestChangesOnApprovalStep';

export const useApprovalData = (
  approvalType: ApprovalType,
  approvalAction: ApprovalAction,
  approvableObjectId: number | undefined,
  assetName: string,
  approvalObjectIds?: number[]
): ApprovalDataEntity => {
  const [hasPendingAgentStep, setHasPendingAgentStep] = useState(false);

  const pollInterval = useAgentApprovalPolling({
    hasPendingAgentStep,
    approvableObjectId,
  });

  const {
    data: queryData,
    error: queryError,
    loading: queryLoading,
    refetch: queryRefetch,
  } = useGetLatestApprovalQuery({
    variables: {
      approvalTypeId: APPROVAL_TYPE_TO_OBJECT_TYPE_ID[approvalType],
      approvalAction,
      approvableObjectId: approvableObjectId!,
    },
    skip: !approvableObjectId,
    /**
     * fetchPolicy 'network-only' ensures fresh data.
     * We cannot remove this as we always need to fetch the latest approval.
     */
    fetchPolicy: 'network-only',
    pollInterval,
    onError: (error) => {
      genericErrorAlert();
      captureException(error, {
        extra: {
          fingerprint: 'useApprovalData-fetchLatest',
          approvalType,
          approvalAction,
          approvableObjectId,
        },
      });
    },
  });

  const fetchError = queryError
    ? (queryError as any)?.networkError?.cause?.errorType ||
      'UNKNOWN_SUBCATEGORY'
    : undefined;

  const actorUserIds = useMemo(() => {
    const approval = queryData?.approval;
    if (!approval) return [];
    const ids: number[] = [];
    if (approval.requester.actorType === ACTOR.USER) {
      ids.push(Number(approval.requester.id));
    }
    for (const step of approval.approvalSteps ?? []) {
      if (step.approver.actorType === ACTOR.USER) {
        ids.push(Number(step.approver.id));
      }
      if (step.actedByUserId != null) {
        ids.push(Number(step.actedByUserId));
      }
    }
    return [...new Set(ids)];
  }, [queryData]);

  const actorAgentIds = useMemo(() => {
    const approval = queryData?.approval;
    if (!approval) return [];
    const ids: string[] = [];
    if (approval.requester.actorType === ACTOR.AGENT) {
      ids.push(approval.requester.id);
    }
    for (const step of approval.approvalSteps ?? []) {
      if (step.approver.actorType === ACTOR.AGENT) {
        ids.push(step.approver.id);
      }
    }
    return [...new Set(ids)];
  }, [queryData]);

  const hasTeamActors = useMemo(() => {
    const approval = queryData?.approval;
    if (!approval) return false;
    return (
      approval.requester.actorType === ACTOR.TEAM ||
      (approval.approvalSteps ?? []).some(
        (step) => step.approver.actorType === ACTOR.TEAM
      )
    );
  }, [queryData]);

  const {
    usersRecord: approverUsers,
    loading: approverUsersLoading,
    error: fetchApproverUsersError,
  } = useAppHubUsers({ userIds: actorUserIds });

  const { agentsRecord: approverAgents, loading: approverAgentsLoading } =
    useBreezeAgents({ agentIds: actorAgentIds });

  const { teamsRecord: approverTeams, loading: approverTeamsLoading } =
    useAppTeams({ enabled: hasTeamActors });

  const resolvedData = useMemo(() => {
    const approval = queryData?.approval;
    if (
      !approval ||
      approverUsersLoading ||
      approverAgentsLoading ||
      (hasTeamActors && approverTeamsLoading)
    )
      return null;
    return toApprovalData(
      approval,
      approverUsers,
      approverAgents,
      approvalType,
      approvalAction,
      approverTeams
    );
  }, [
    queryData,
    approverUsers,
    approverUsersLoading,
    approverAgents,
    approverAgentsLoading,
    approverTeams,
    approverTeamsLoading,
    hasTeamActors,
    approvalType,
    approvalAction,
  ]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHasPendingAgentStep(
      hasPendingAgentApprovalStep(resolvedData?.approvalSteps ?? [])
    );
  }, [resolvedData]);

  const fetchLatest = useCallback(() => {
    void queryRefetch();
  }, [queryRefetch]);

  const create = useCreateApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    assetName,
    approvalObjectIds,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
  });

  const approve = useApproveApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData,
  });

  const reject = useRejectApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData,
  });

  const cancel = useCancelApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData,
  });

  const update = useUpdateApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData,
  });

  const requestChanges = useRequestChangesOnApproval({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData,
  });

  const reRequestReview = useReRequestReview({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData,
  });

  const approveStep = useApproveApprovalStep({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData,
  });

  const rejectStep = useRejectApprovalStep({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData,
  });

  const requestChangesStep = useRequestChangesOnApprovalStep({
    approvalType,
    approvalAction,
    approvableObjectId,
    usersRecord: approverUsers,
    agentsRecord: approverAgents,
    teamsRecord: approverTeams,
    resolvedData,
  });

  const api = useMemo(
    () => ({
      fetchLatest,
      create,
      cancel,
      reject,
      approve,
      update,
      requestChanges,
      reRequestReview,
      approveStep,
      rejectStep,
      requestChangesStep,
    }),
    [
      fetchLatest,
      create,
      cancel,
      reject,
      approve,
      update,
      requestChanges,
      reRequestReview,
      approveStep,
      rejectStep,
      requestChangesStep,
    ]
  );

  return {
    data: resolvedData,
    isLoading:
      queryLoading ||
      approverUsersLoading ||
      approverAgentsLoading ||
      (hasTeamActors && approverTeamsLoading),
    api,
    fetchError,
    hasFetchApproverUsersError: Boolean(fetchApproverUsersError),
  };
};
